"use client";

import { useState, useMemo } from "react";
import { useAuth } from "@/app/context/AuthContext";
import axios, { AxiosError } from "axios";
import { PALETTE } from "@/app/lib/colorPallete";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, Loader2, UploadCloud, Mail, User, Phone, CheckCircle2, XCircle } from "lucide-react";
import { PasswordField } from "./PasswordField";

const PasswordStrength = ({ password }: { password: string }) => {
  const strength = useMemo(() => {
    if (!password) return { level: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { level: 1, label: 'Weak', color: 'bg-red-400' };
    if (score <= 3) return { level: 2, label: 'Medium', color: 'bg-amber-400' };
    return { level: 3, label: 'Strong', color: 'bg-green-400' };
  }, [password]);

  if (!password) return null;

  return (
    <div className="space-y-1.5 mt-2">
      <div className="flex gap-1">
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
              i <= strength.level ? strength.color : 'bg-stone-200'
            }`}
          />
        ))}
      </div>
      <p className={`text-xs font-medium ${
        strength.level === 1 ? 'text-red-500' : strength.level === 2 ? 'text-amber-500' : 'text-green-500'
      }`}>
        {strength.label}
      </p>
    </div>
  );
};

export const RegisterForm = ({
  onSwitchToLogin,
  onSuccess,
}: {
  onSwitchToLogin: () => void;
  onSuccess: () => void;
}) => {
  const { setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    gstNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [isWholesale, setIsWholesale] = useState(false);
  const [idCardFile, setIdCardFile] = useState<File | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIdCardFile(e.target.files[0]);
    }
  };

  const getFieldError = (field: string) => validationErrors[field]?.join(', ');

  const passwordsMatch = formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;
  const passwordsMismatch = formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (formData.password !== formData.confirmPassword) {
      setValidationErrors({ confirmPassword: ["Passwords do not match."] });
      return;
    }
    if (isWholesale && !idCardFile) {
      setValidationErrors({
        wholesaleIdCard: [
          "Please upload your business ID card to register as a wholesaler.",
        ],
      });
      return;
    }
    if (isWholesale && formData.gstNumber) {
      const gst = formData.gstNumber.toUpperCase();
      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
      if (!gstRegex.test(gst)) {
        setValidationErrors({
          gstNumber: ["Invalid GST format. Example: 22AAAAA0000A1Z5"],
        });
        return;
      }
    }
    setValidationErrors({});
    setIsLoading(true);

    const apiFormData = new FormData();
    const bodyPayload = {
      ...formData,
      isWholesale,
      gstNumber: isWholesale ? formData.gstNumber : undefined,
    };
    apiFormData.append("body", JSON.stringify(bodyPayload));
    if (isWholesale && idCardFile) {
      apiFormData.append("wholesaleIdCard", idCardFile);
    }

    try {
      const response = await axios.post("/api/user/register", apiFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.user) {
        setUser(response.data.user);
        onSuccess();
      }
    } catch (err) {
      const axiosError = err as AxiosError<{
        message: string;
        errors?: Record<string, string[]>;
      }>;
      const errorData = axiosError.response?.data;
      if (errorData?.errors) {
        setValidationErrors(errorData.errors);
      } else {
        setError(errorData?.message || "An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      key="register"
      initial={{ x: "100%", opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "-100%", opacity: 0 }}
      transition={{ type: "tween", ease: "easeInOut" }}
    >
      <h2
        className="text-2xl font-bold text-center font-serif"
        style={{ color: PALETTE.darkGreen }}
      >
        Create an Account
      </h2>
      <p className="text-center text-stone-500 mb-6 mt-2">
        Join us to start customizing.
      </p>

      {error && !Object.keys(validationErrors).length && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600 text-center">{error}</p>
        </div>
      )}

      {Object.keys(validationErrors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="text-red-800 font-semibold mb-2 text-sm flex items-center gap-2">
            Registration Errors
          </h3>
          <ul className="list-disc list-inside space-y-1 text-xs text-red-600">
            {Object.entries(validationErrors).map(([field, messages]) => (
              <li key={field}>
                <span className="font-semibold capitalize">
                  {field.replace(/([A-Z])/g, " $1").trim()}
                </span>
                : {messages.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-stone-600">Full Name</label>
          <div className="relative">
            <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <input
              type="text"
              name="name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              required
              className={`w-full p-3 pl-10 rounded-md bg-stone-100 border focus:ring-2 transition-colors text-stone-900 placeholder:text-stone-400 ${
                getFieldError('name') ? 'border-red-400 focus:ring-red-400/50' : 'border-stone-300 focus:ring-amber-500/80'
              }`}
            />
          </div>
          {getFieldError('name') && <p className="text-xs text-red-500">{getFieldError('name')}</p>}
        </div>

        {/* Email */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-stone-600">Email Address</label>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <input
              type="email"
              name="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              className={`w-full p-3 pl-10 rounded-md bg-stone-100 border focus:ring-2 transition-colors text-stone-900 placeholder:text-stone-400 ${
                getFieldError('email') ? 'border-red-400 focus:ring-red-400/50' : 'border-stone-300 focus:ring-amber-500/80'
              }`}
            />
          </div>
          {getFieldError('email') && <p className="text-xs text-red-500">{getFieldError('email')}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-stone-600">Phone Number</label>
          <div className="relative">
            <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <input
              type="tel"
              name="phone"
              placeholder="9876543210"
              value={formData.phone}
              onChange={handleChange}
              required
              className={`w-full p-3 pl-10 rounded-md bg-stone-100 border focus:ring-2 transition-colors text-stone-900 placeholder:text-stone-400 ${
                getFieldError('phone') ? 'border-red-400 focus:ring-red-400/50' : 'border-stone-300 focus:ring-amber-500/80'
              }`}
            />
          </div>
          <p className="text-xs text-stone-400">10-digit Indian mobile number</p>
          {getFieldError('phone') && <p className="text-xs text-red-500">{getFieldError('phone')}</p>}
        </div>

        {/* Password */}
        <div>
          <PasswordField label="Password" value={formData.password} onChange={handleChange} error={getFieldError('password')} />
          <PasswordStrength password={formData.password} />
        </div>

        {/* Confirm Password */}
        <div className="space-y-1.5">
          <PasswordField
            label="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm Password"
            name="confirmPassword"
            error={getFieldError('confirmPassword')}
          />
          {formData.confirmPassword && (
            <div className="flex items-center gap-1.5 mt-1">
              {passwordsMatch ? (
                <>
                  <CheckCircle2 size={14} className="text-green-500" />
                  <span className="text-xs text-green-500 font-medium">Passwords match</span>
                </>
              ) : passwordsMismatch ? (
                <>
                  <XCircle size={14} className="text-red-500" />
                  <span className="text-xs text-red-500 font-medium">Passwords don&apos;t match</span>
                </>
              ) : null}
            </div>
          )}
        </div>

        {process.env.NEXT_PUBLIC_ENABLE_WHOLESALE === 'true' && (
          <>
            <div className="pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isWholesale}
                  onChange={(e) => setIsWholesale(e.target.checked)}
                  className="h-4 w-4 accent-amber-500"
                />
                <span className="text-sm font-medium text-stone-700">
                  Register as a wholesale customer?
                </span>
              </label>
            </div>

            <AnimatePresence>
              {isWholesale && (
                <motion.div
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: "auto", opacity: 1, marginTop: "1rem" }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  className="space-y-4"
                >
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-stone-600">GST Number</label>
                    <input
                      type="text"
                      name="gstNumber"
                      placeholder="e.g., 22AAAAA0000A1Z5"
                      value={formData.gstNumber}
                      onChange={handleChange}
                      required
                      className={`w-full p-3 rounded-md bg-stone-100 border focus:ring-2 transition-colors text-stone-900 placeholder:text-stone-400 uppercase ${
                        getFieldError('gstNumber') ? 'border-red-400 focus:ring-red-400/50' : 'border-stone-300 focus:ring-amber-500/80'
                      }`}
                      maxLength={15}
                    />
                    <p className="text-xs text-stone-400">
                      15-character GST Identification Number
                    </p>
                    {getFieldError('gstNumber') && <p className="text-xs text-red-500">{getFieldError('gstNumber')}</p>}
                  </div>
                  <label className={`relative p-4 border-2 border-dashed rounded-lg text-center cursor-pointer hover:border-amber-500 hover:bg-stone-50 transition-all flex flex-col items-center justify-center ${
                    getFieldError('wholesaleIdCard') ? 'border-red-400' : 'border-stone-300'
                  }`}>
                    <UploadCloud className="mx-auto h-8 w-8 text-stone-400" />
                    <span className="text-sm mt-2 font-semibold text-stone-600">
                      {idCardFile ? "File Selected" : "Upload Business ID Card"}
                    </span>
                    {idCardFile ? (
                      <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                        <FileText size={12} /> {idCardFile.name}
                      </p>
                    ) : (
                      <p className="text-xs text-stone-500 mt-1">
                        PDF, JPG, PNG up to 5MB
                      </p>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0"
                    />
                  </label>
                  {getFieldError('wholesaleIdCard') && <p className="text-xs text-red-500">{getFieldError('wholesaleIdCard')}</p>}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 mt-2 rounded-full font-semibold text-white flex items-center justify-center gap-2 disabled:bg-stone-400 transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{ backgroundColor: PALETTE.darkerGreen }}
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin" size={20} /> Creating Account...
            </>
          ) : (
            "Sign Up"
          )}
        </button>
      </form>
      <p className="text-center text-sm text-stone-500 mt-6">
        Already have an account?{" "}
        <button
          onClick={onSwitchToLogin}
          className="font-semibold text-amber-600 hover:underline"
        >
          Sign In
        </button>
      </p>
    </motion.div>
  );
};
